'use client';

import React, { useState, Suspense } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import AppLogo from '@/components/ui/AppLogo';
import {
  Eye,
  EyeOff,
  AlertCircle,
  Clock,
  TrendingUp,
  Shield,
  BarChart2,
  Users,
  ArrowRight,
} from 'lucide-react';
import { useSearchParams } from 'next/navigation';

function SessionExpiredBanner() {
  const searchParams = useSearchParams();
  const sessionExpired = searchParams.get('reason') === 'session_timeout';
  if (!sessionExpired) return null;
  return (
    <div className="flex items-start gap-3 p-4 rounded-xl mb-5 border border-amber-400/30 bg-amber-400/10">
      <Clock className="w-5 h-5 mt-0.5 flex-shrink-0 text-amber-400" />
      <div>
        <p className="text-sm font-semibold text-amber-400">Session Expired</p>
        <p className="text-xs mt-0.5 text-gray-400">
          You were automatically logged out after 30 minutes of inactivity. Please sign in again.
        </p>
      </div>
    </div>
  );
}

const features = [
  {
    icon: TrendingUp,
    title: 'Strategic Pool Management',
    desc: 'Allocate and track profit pools in real time',
  },
  {
    icon: BarChart2,
    title: 'Scenario Forecasting',
    desc: 'AI-powered financial projections and analysis',
  },
  {
    icon: Shield,
    title: 'Risk Management',
    desc: 'Monitor and mitigate financial risks proactively',
  },
  {
    icon: Users,
    title: 'Team Compensation',
    desc: 'Transparent reward and recognition tracking',
  },
];

const stats = [
  { value: '99.9%', label: 'Uptime' },
  { value: '5 Pools', label: 'Managed' },
  { value: '3.2x', label: 'ROI Growth' },
];

export default function LoginPage() {
  const router = useRouter();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
      router.replace('/');
    } catch (err: any) {
      setError(err?.message || 'Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* ── Left Panel ── */}
      <div
        className="hidden lg:flex lg:w-[54%] flex-col justify-between p-10 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #5b4fcf 0%, #7c6fe0 40%, #6c63d8 100%)',
        }}
      >
        {/* Decorative blobs */}
        <div
          className="absolute top-[-80px] right-[-80px] w-72 h-72 rounded-full opacity-20"
          style={{ background: 'rgba(255,255,255,0.25)' }}
        />
        <div
          className="absolute bottom-[-60px] left-[-60px] w-56 h-56 rounded-full opacity-15"
          style={{ background: 'rgba(255,255,255,0.2)' }}
        />
        <div
          className="absolute top-1/2 right-[-40px] w-40 h-40 rounded-full opacity-10"
          style={{ background: 'rgba(255,255,255,0.3)' }}
        />

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
            <AppLogo size={28} />
          </div>
          <div>
            <p className="text-white font-bold text-lg leading-tight">Flourish.AI</p>
            <p className="text-white/70 text-xs">eCity For Financial Services</p>
          </div>
        </div>

        {/* Main copy */}
        <div className="relative z-10 flex-1 flex flex-col justify-center py-10">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 w-fit mb-6">
            <BarChart2 className="w-3.5 h-3.5 text-white/80" />
            <span className="text-white/90 text-xs font-medium">AI-Native Financial OS</span>
          </div>

          <h1 className="text-4xl font-bold text-white leading-tight mb-4">
            Your profits,<br />
            strategically managed.
          </h1>
          <p className="text-white/75 text-sm leading-relaxed max-w-sm mb-8">
            Flourish.AI allocates profits into strategic pools, monitors performance,
            and surfaces insights — so leadership acts only when it matters.
          </p>

          {/* Feature grid */}
          <div className="grid grid-cols-2 gap-3">
            {features.map((f) => (
              <div
                key={f.title}
                className="rounded-xl p-3.5 bg-white/10 border border-white/15 backdrop-blur-sm"
              >
                <f.icon className="w-4 h-4 text-white/80 mb-2" />
                <p className="text-white text-xs font-semibold leading-tight">{f.title}</p>
                <p className="text-white/60 text-xs mt-0.5">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Stats row */}
        <div className="relative z-10 flex items-center gap-8">
          {stats.map((s, i) => (
            <div key={s.label}>
              <p className="text-white font-bold text-xl">{s.value}</p>
              <p className="text-white/60 text-xs">{s.label}</p>
              {i < stats.length - 1 && (
                <span className="sr-only">|</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── Right Panel ── */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 bg-gray-50 dark:bg-[#0f0f13]">
        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2 mb-8">
          <AppLogo size={32} />
          <span className="font-bold text-lg" style={{ color: 'var(--foreground)' }}>
            Flourish<span style={{ color: 'var(--primary)' }}>.AI</span>
          </span>
        </div>

        <div className="w-full max-w-[400px]">
          <h2 className="text-2xl font-bold mb-1" style={{ color: 'var(--foreground)' }}>
            Welcome back
          </h2>
          <p className="text-sm mb-8" style={{ color: 'var(--muted-foreground)' }}>
            Sign in to your financial management portal
          </p>

          {/* Session timeout notice */}
          <Suspense fallback={null}>
            <SessionExpiredBanner />
          </Suspense>

          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg mb-5 text-sm bg-red-500/10 border border-red-500/20 text-red-500">
              <AlertCircle size={15} className="flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label
                className="block text-sm font-medium mb-1.5"
                style={{ color: 'var(--foreground)' }}
              >
                Work Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@ecity.ai"
                required
                className="w-full px-4 py-2.5 rounded-lg text-sm outline-none transition-all"
                style={{
                  background: 'var(--card)',
                  border: '1.5px solid var(--border)',
                  color: 'var(--foreground)',
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = '#7c6fe0'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
              />
            </div>

            {/* Password */}
            <div>
              <label
                className="block text-sm font-medium mb-1.5"
                style={{ color: 'var(--foreground)' }}
              >
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full px-4 py-2.5 pr-11 rounded-lg text-sm outline-none transition-all"
                  style={{
                    background: 'var(--card)',
                    border: '1.5px solid var(--border)',
                    color: 'var(--foreground)',
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = '#7c6fe0'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: 'var(--muted-foreground)' }}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Remember me */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border accent-[#7c6fe0]"
                  style={{ borderColor: 'var(--border)' }}
                />
                <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
                  Remember me
                </span>
              </label>
              <button
                type="button"
                className="text-sm font-medium transition-opacity hover:opacity-80"
                style={{ color: '#7c6fe0' }}
              >
                Forgot password?
              </button>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all duration-150 shadow-md"
              style={{
                background: loading
                  ? 'var(--muted)'
                  : 'linear-gradient(135deg, #5b4fcf 0%, #7c6fe0 100%)',
                color: loading ? 'var(--muted-foreground)' : '#fff',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Signing in...
                </span>
              ) : (
                <>
                  Sign In to Flourish.AI
                  <ArrowRight size={15} />
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <p className="text-xs text-center mt-8" style={{ color: 'var(--muted-foreground)' }}>
            By signing in, you agree to our{' '}
            <span className="underline cursor-pointer" style={{ color: '#7c6fe0' }}>
              Terms of Service
            </span>{' '}
            and{' '}
            <span className="underline cursor-pointer" style={{ color: '#7c6fe0' }}>
              Privacy Policy
            </span>
          </p>
        </div>
      </div>
    </div>
  );
}
